import { reactMount } from '@/lib/reactMount';
import { useId, useRef } from 'react';
import { Media } from '@/components/Media';
import { ButtonExpand } from '@/components/ButtonExpand';
import { animationSettings } from '@/animationSettings';
import { DivMotion } from '@/components/motionComponents';

const StackingCards = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco));
  umbraco ||= umbracoRef.current;
  umbraco.BlockId = useId() + 'StackingCards';
  const theme = umbraco?.Theme || 'rose-light';
  const cardTheme = umbraco?.CardTheme || 'emerald-dark';
  const cardCount = umbraco?.Cards?.length;

  const { standardTransition, standardViewportTrigger } = animationSettings();

  let gridCols = '';
  if (cardCount === 2) {
    gridCols = 'md:grid-cols-2';
  }
  if (cardCount === 3) {
    gridCols = 'lg:grid-cols-3';
  }
  if (cardCount === 4) {
    gridCols = 'lg:grid-cols-2';
  }
  if (cardCount >= 5) {
    gridCols = 'lg:grid-cols-3';
  }

  return (
    <section
      id={umbraco?.AnchorTarget || null}
      data-block-id={umbraco.BlockId}
      data-theme={theme}
      className="py-16 px-6 md:px-8 bg-[--theme-bg] text-[--theme-text] overflow-hidden"
    >
      {(umbraco.Headline || umbraco.Summary) && (
        <header className="mx-auto max-w-5xl pb-14">
          <DivMotion
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={standardTransition}
            viewport={{ once: true, amount: standardViewportTrigger }}
            className="mx-auto text-center px-2.5"
          >
            {umbraco.Headline && (
              <h2 className="text-pretty text-4xl font-serif tracking-tight sm:text-balance md:mt-2 sm:text-5xl">
                {umbraco.Headline}
              </h2>
            )}

            {umbraco.Summary && (
              <div
                className="_rte mt-2 md:mt-4 text-pretty text-base sm:text-xl font-light max-w-[556px] mx-auto"
                dangerouslySetInnerHTML={{ __html: umbraco.Summary }}
              />
            )}
          </DivMotion>
        </header>
      )}

      <div className={`mx-auto max-w-screen-xl grid gap-5 ${gridCols}`}>
        {umbraco?.Cards.map((Card, index) => (
          <div key={index} className="w-full h-auto flex">
            <CardItem card={Card} cardIndex={index} theme={cardTheme} />
          </div>
        ))}
      </div>
    </section>
  );
};

const CardItem = ({ theme, card, cardIndex, initialLoad = true }) => {
  const {
    standardTransitionDistance,
    standardViewportTrigger,
    standardTransitionEasing,
    standardTransitionDuration,
    standardTransitionDelay,
    standardTransitionStagger,
  } = animationSettings();

  return (
    <DivMotion
      data-theme={theme}
      initial={{ translateY: standardTransitionDistance, opacity: 0 }}
      whileInView={{ translateY: '0', opacity: 1 }}
      transition={{
        duration: initialLoad ? standardTransitionDuration : 0,
        delay: initialLoad
          ? standardTransitionDelay + cardIndex * standardTransitionStagger
          : 0,
        ease: standardTransitionEasing,
      }}
      viewport={{ once: true, amount: standardViewportTrigger }}
      className="relative isolate justify-end text-[--theme-reversed-text] hover:text-[--theme-text] w-full"
    >
      <div
        className={`group/link h-full w-full flex flex-col rounded-3xl overflow-hidden top-0 left-0 border border-[--theme-reversed-text] hover:border-[--theme-text] transition duration-300 bg-transparent  hover:bg-[--custom-hover-bg]
      `}
      >
        {card.Button && (
          <a
            aria-label={card.Button.Name}
            href={card.Button.Url}
            target={card.Button.Target}
            className="absolute inset-0 z-10"
          >
            <span className="sr-only">{card.Button.Name}</span>
          </a>
        )}
        {card.Media && (
          <div className="px-8 pt-8">
            <div
              className={`relative aspect-[6/4] w-full rounded-[8px] overflow-hidden flex justify-center items-center ${card.Button ? '_media-inactive' : ''}`}
            >
              <Media umbraco={card} isVideoPreview={!!card?.Button?.Url} />
            </div>
          </div>
        )}
        <div
          className={`relative md:mx-0 px-8 pt-6 pb-8 flex flex-col md:text-left w-full ${card.Media ? 'justify-end' : 'h-full'} ${!card.Media && card.Button ? 'pb-16' : ''}`}
        >
          {card.Label && (
            <p className="text-pretty text-sm sm:text-base mb-1">
              {card.Label}
            </p>
          )}
          {card.Headline && (
            <h2 className="text-balance text-xl sm:text-3xl font-serif tracking-tight mb-2.5">
              {card.Headline}
            </h2>
          )}
          {card.Summary && (
            <div
              className="text-pretty text-sm sm:text-base _rte"
              dangerouslySetInnerHTML={{ __html: card.Summary }}
            />
          )}
        </div>
        {card.Button && (
          <div className="relative w-full grow pb-11">
            <div className="absolute left-8 bottom-7 flex justify-start">
              <ButtonExpand href={card.Button.Url} target={card.Button.Target}>
                {card.Button.Name}
              </ButtonExpand>
            </div>
          </div>
        )}
      </div>
    </DivMotion>
  );
};

reactMount('StackingCards', StackingCards);
